import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import type { Building } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import { Card, CardHeader, CardTitle } from '../EnergyTab.styles'

interface SolarComparisonCardProps {
  building: Building
  t: TFunction
  className?: string
}

// --- Layout ---

const DiagramRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.default};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const MetricBoxesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.default};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

// --- Metric Boxes ---

const MetricBox = styled.div<{ $variant: 'all' | 'high' }>`
  padding: ${({ theme }) => theme.spacing.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ $variant, theme }) =>
    $variant === 'all' ? theme.colors.background.secondary : '#fffbeb'};
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === 'all' ? theme.colors.border.light : '#f59e0b'};
`

const MetricBoxTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const MetricItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const MetricLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 4px;
`

const MetricValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
`

// --- Legend ---

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const LegendTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LegendSwatch = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const ThresholdLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`

const ThresholdDash = styled.div`
  width: 12px;
  height: 0;
  border-top: 2px dashed ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
`

// --- Note ---

const NoteBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-left: 3px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

// --- SVG Roof Diagram ---

const RoofDiagram: React.FC = () => (
  <svg
    viewBox="0 0 280 180"
    width="280"
    height="180"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="Roof solar irradiance diagram"
    style={{ flexShrink: 0 }}
  >
    {/* Sun */}
    <circle cx="240" cy="30" r="16" fill="#FCAA00" opacity="0.9" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
      const rad = (angle * Math.PI) / 180
      const x1 = 240 + Math.cos(rad) * 22
      const y1 = 30 + Math.sin(rad) * 22
      const x2 = 240 + Math.cos(rad) * 28
      const y2 = 30 + Math.sin(rad) * 28
      return (
        <line
          key={angle}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#FCAA00"
          strokeWidth="2"
          strokeLinecap="round"
          opacity="0.7"
        />
      )
    })}

    {/* Building body */}
    <rect
      x="40"
      y="110"
      width="200"
      height="55"
      rx="2"
      fill="#e2e8f0"
      stroke="#94a3b8"
      strokeWidth="1"
    />

    {/* Roof sections — colored by irradiance (5 levels) */}
    {/* Excellent ≥1400 */}
    <polygon points="140,50 40,110 80,110" fill="#A80200" />
    {/* Very Good 1200-1400 */}
    <polygon points="140,50 80,110 120,110" fill="#FA5502" />
    {/* Good 1000-1200 */}
    <polygon points="140,50 120,110 160,110" fill="#FCAA00" />
    {/* Moderate 800-1000 */}
    <polygon points="140,50 160,110 200,110" fill="#FFFC09" />
    {/* Low <800 */}
    <polygon points="140,50 200,110 240,110" fill="#2DC5FF" />

    {/* Roof outline */}
    <polyline
      points="40,110 140,50 240,110"
      fill="none"
      stroke="#475569"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />

    {/* Threshold dashed line at 1000 kWh boundary (between Good and Moderate) */}
    <line
      x1="160"
      y1="110"
      x2="140"
      y2="50"
      stroke="#475569"
      strokeWidth="1.5"
      strokeDasharray="4 3"
      opacity="0.7"
    />

    {/* Threshold label */}
    <text x="160" y="75" fontSize="9" fill="#475569" fontWeight="500">
      1000 kWh/m²
    </text>

    {/* Windows */}
    <rect
      x="70"
      y="125"
      width="22"
      height="28"
      rx="1"
      fill="#bfdbfe"
      stroke="#94a3b8"
      strokeWidth="0.5"
    />
    <rect
      x="130"
      y="125"
      width="22"
      height="28"
      rx="1"
      fill="#bfdbfe"
      stroke="#94a3b8"
      strokeWidth="0.5"
    />
    <rect
      x="190"
      y="125"
      width="22"
      height="28"
      rx="1"
      fill="#bfdbfe"
      stroke="#94a3b8"
      strokeWidth="0.5"
    />
  </svg>
)

// --- Tooltip helper ---

const TooltipForMetric: React.FC<{
  t: TFunction
  metricKey: string
}> = ({ t, metricKey }) => (
  <InfoTooltip>
    <TooltipMetricExplanation
      title={t(`energy.solarComparison.tooltips.${metricKey}.title`)}
      shortDescription={t(
        `energy.solarComparison.tooltips.${metricKey}.description`
      )}
      calculation={t(
        `energy.solarComparison.tooltips.${metricKey}.calculation`
      )}
      importance={t(`energy.solarComparison.tooltips.${metricKey}.importance`)}
      limitations={t(
        `energy.solarComparison.tooltips.${metricKey}.limitations`
      )}
      source={t(`energy.solarComparison.tooltips.${metricKey}.source`)}
    />
  </InfoTooltip>
)

// --- Main Component ---

export const SolarComparisonCard: React.FC<SolarComparisonCardProps> = ({
  building,
  t,
  className,
}) => {
  const {
    solar_area,
    solar_area_best,
    solar_average_exposure,
    solar_average_exposure_best,
    solar_global_exposure_best,
    solar_electrical_efficiency,
    solar_electrical_efficiency_best,
  } = building

  const hasActualData = solar_area && solar_electrical_efficiency
  const hasOptimalData = solar_area_best && solar_electrical_efficiency_best

  if (!hasActualData && !hasOptimalData) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>
            <Icon name="sun" size="small" />
            {t('solarComparison.title')}
          </CardTitle>
        </CardHeader>
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          {t('solarComparison.noData')}
        </div>
      </Card>
    )
  }

  const estimatedCapacityActual = solar_area ? solar_area / 6.5 : 0
  const estimatedCapacityOptimal = solar_area_best ? solar_area_best / 6.5 : 0

  // Derive best average irradiance: use API value, or compute from total / area
  const bestIrradiance =
    solar_average_exposure_best ||
    (solar_global_exposure_best && solar_area_best
      ? solar_global_exposure_best / solar_area_best
      : undefined)

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <Icon name="sun" size="small" />
          {t('solarComparison.title')}
        </CardTitle>
      </CardHeader>

      {/* Row 1: Roof diagram + Color legend side by side */}
      <DiagramRow>
        <RoofDiagram />

        <LegendContainer>
          <LegendTitle>{t('solarComparison.legendTitle')}</LegendTitle>
          <LegendRow>
            <LegendSwatch $color="#A80200" />
            {t('solarComparison.legendExcellent')}
          </LegendRow>
          <LegendRow>
            <LegendSwatch $color="#FA5502" />
            {t('solarComparison.legendVeryGood')}
          </LegendRow>
          <LegendRow>
            <LegendSwatch $color="#FCAA00" />
            {t('solarComparison.legendGood')}
          </LegendRow>
          <LegendRow>
            <LegendSwatch $color="#FFFC09" />
            {t('solarComparison.legendModerate')}
          </LegendRow>
          <LegendRow>
            <LegendSwatch $color="#2DC5FF" />
            {t('solarComparison.legendLow')}
          </LegendRow>
          <ThresholdLegendRow>
            <ThresholdDash />
            {t('solarComparison.thresholdLabel')}
          </ThresholdLegendRow>
        </LegendContainer>
      </DiagramRow>

      {/* Row 2: Metric boxes */}
      <MetricBoxesRow>
        {/* All Suitable Surfaces */}
        {hasActualData && (
          <MetricBox $variant="all">
            <MetricBoxTitle>
              <Icon name="sun" size="small" />
              {t('solarComparison.actualConditions')}
            </MetricBoxTitle>

            <MetricItem>
              <MetricLabel>
                {t('solarComparison.suitableRoofArea')}
                <TooltipForMetric t={t} metricKey="suitableRoofArea" />
              </MetricLabel>
              <MetricValue>
                {Math.round(solar_area).toLocaleString('de-CH')} m²
              </MetricValue>
            </MetricItem>

            {solar_average_exposure && (
              <MetricItem>
                <MetricLabel>
                  {t('solarComparison.solarIrradiance')}
                  <TooltipForMetric t={t} metricKey="solarIrradiance" />
                </MetricLabel>
                <MetricValue>
                  {Math.round(solar_average_exposure).toLocaleString('de-CH')}{' '}
                  kWh/m²/yr
                </MetricValue>
              </MetricItem>
            )}

            <MetricItem>
              <MetricLabel>
                {t('solarComparison.annualProductionPotential')}
                <TooltipForMetric t={t} metricKey="annualProduction" />
              </MetricLabel>
              <MetricValue>
                {Math.round(solar_electrical_efficiency).toLocaleString(
                  'de-CH'
                )}{' '}
                kWh/yr
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>
                {t('solarComparison.estimatedCapacity')}
                <TooltipForMetric t={t} metricKey="estimatedCapacity" />
              </MetricLabel>
              <MetricValue>
                {estimatedCapacityActual.toFixed(1)} kWp
              </MetricValue>
            </MetricItem>
          </MetricBox>
        )}

        {/* High-Potential Only */}
        {hasOptimalData && (
          <MetricBox $variant="high">
            <MetricBoxTitle>
              <Icon name="zap" size="small" />
              {t('solarComparison.optimalConditions')}
            </MetricBoxTitle>

            <MetricItem>
              <MetricLabel>
                {t('solarComparison.bestSuitableArea')}
                <TooltipForMetric t={t} metricKey="bestSuitableArea" />
              </MetricLabel>
              <MetricValue>
                {Math.round(solar_area_best).toLocaleString('de-CH')} m²
              </MetricValue>
            </MetricItem>

            {bestIrradiance && (
              <MetricItem>
                <MetricLabel>
                  {t('solarComparison.bestSolarIrradiance')}
                  <TooltipForMetric t={t} metricKey="bestSolarIrradiance" />
                </MetricLabel>
                <MetricValue>
                  {Math.round(bestIrradiance).toLocaleString('de-CH')} kWh/m²/yr
                </MetricValue>
              </MetricItem>
            )}

            <MetricItem>
              <MetricLabel>
                {t('solarComparison.bestAnnualProduction')}
                <TooltipForMetric t={t} metricKey="bestAnnualProduction" />
              </MetricLabel>
              <MetricValue>
                {Math.round(solar_electrical_efficiency_best).toLocaleString(
                  'de-CH'
                )}{' '}
                kWh/yr
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>
                {t('solarComparison.estimatedCapacity')}
                <TooltipForMetric t={t} metricKey="estimatedCapacityOptimal" />
              </MetricLabel>
              <MetricValue>
                {estimatedCapacityOptimal.toFixed(1)} kWp
              </MetricValue>
            </MetricItem>
          </MetricBox>
        )}
      </MetricBoxesRow>

      {/* Row 3: Note */}
      {hasActualData && hasOptimalData && (
        <NoteBox>
          <strong>{t('solarComparison.noteTitle')}</strong>
          <br />
          {t('solarComparison.noteDescription')}
        </NoteBox>
      )}
    </Card>
  )
}
